import { handleAxiosError, like } from '@/api/helpers';
import { Account } from '@/client/accounts';
import {
  ChangeEnrollDateFormValues,
  CourseEnrollmentTypeEnum,
  CourseEntityEnrollEnum,
  CourseEntityScheduleEnum,
  CourseScheduleType,
  CourseUser,
  EnrollFormValues,
  ForceCompleteFormValues,
  MaterialType,
  UserStatusEnum,
} from '@/client/courses';
import { FilterNamesEnum, getFiltersFromColumns } from '@/client/helpers';
import { HubspotProperty, HubspotValue } from '@/client/hubspot/types';
import { Actions, Subjects, User } from '@/client/users';
import { TableNamesEnum } from '@/common/constants';
import { userStatusOptions } from '@/common/constants/enroll-types';
import {
  notSystemRoleOptions,
  roleTranslations,
} from '@/common/constants/roles';
import { DialogContext } from '@/common/context';
import { ForceCompleteModal } from '@/components/courses/modals';
import { ChangeEnrollDateModal } from '@/components/courses/modals/ChangeEnrollDateModal';
import { EnrollModal } from '@/components/courses/modals/EnrollModal';
import {
  DataTable,
  DataTableActions,
  DataTableColumnsMultiselect,
  DataTableColumnType,
  DataTableFilters,
  DataTableToolbar,
  FilterTypeEnum,
} from '@/components/tables/crud';
import {
  useChangeScheduleDate,
  useCourse,
  useCourseUsers,
  useDeleteEnrollment,
  useForceComplete,
  useUsersSchedules,
} from '@/hooks/query';
import { useAppSelector } from '@/hooks/store';
import { useTable } from '@/hooks/table.hook';
import { usePermission } from '@/hooks/usePermission';
import { useToast } from '@/hooks/useToast';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUserState } from '@/store/features/users';
import { DateFormats, DialogType } from '@/system-settings/enums';
import { FormatDate } from '@/ui/date';
import { FlexContainer } from '@/ui/styled-ui';
import { getCourseUserRowKey, showBlockEmailsMessage } from '@/utils/helpers';
import { displayFullName } from '@/utils/helpers/display.helper';
import { isDemoRestrictedMode } from '@/utils/helpers/env.helper';
import { determineUserStatusColor } from '@/utils/helpers/ui.helper';
import { hubspotTrack } from '@/utils/hubspot/hubspot.helper';
import { AxiosError } from 'axios';
import { debounce } from 'lodash';
import moment from 'moment';
import { InputText } from 'primereact/inputtext';
import { MenuItem } from 'primereact/menuitem';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { FormEvent, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { CoursesActionHeader } from './CoursesActionHeader';
import { CoursesTabs } from './CoursesTabs';

export const CourseUsersPage: React.FC<{ type: MaterialType }> = ({ type }) => {
  const { t } = useTranslation();
  const { id: courseId } = useParams();
  const toast = useToast();
  const { user } = useAppSelector(selectCurrentUserState);
  const account = useAppSelector(selectCurrentAccount);
  const isDemoAccount = isDemoRestrictedMode(account, user);
  const { setDialogData } = useContext(DialogContext);
  const { can } = usePermission();
  const isMaterial = type !== MaterialType.COURSE;
  const { course, isLoading: isCourseLoading } = useCourse({
    courseId: courseId,
  });

  const { skip, take, sort, apiFilters, onFilter, onSort, onPage, setSkip } =
    useTable({ tableName: TableNamesEnum.COURSE_USERS });

  const [selectedUser, setSelectedUser] = useState<CourseUser | undefined>(
    undefined,
  );
  const [showChangeEnrollDateModal, setShowChangeEnrollDateModal] =
    useState(false);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [showForceCompleteModal, setShowForceCompleteModal] = useState(false);

  const [multiSearchValue, setMultiSearchValue] = useState('');
  const debouncedSetMultiSearchValue = useMemo(
    () =>
      debounce((event: FormEvent) => {
        setSkip(0);
        setMultiSearchValue((event.target as HTMLInputElement).value);
      }, 300),
    [],
  );

  const {
    isLoading: isCourseUsersLoading,
    courseUsers,
    refetch,
  } = useCourseUsers(
    {
      take,
      skip,
      filters: [
        ...apiFilters,
        ...(multiSearchValue.length ? [like('name', multiSearchValue)] : []),
      ],
      sort: sort && sort.length > 0 ? [sort.join(',')] : [],
      enabled: true,
    },
    courseId,
    account?.id,
  );

  const changeScheduleDate = useChangeScheduleDate();
  const handleChangeScheduleDate = async (data: ChangeEnrollDateFormValues) => {
    if (isDemoAccount) return;
    if (selectedUser?.schedule && data.date) {
      try {
        await changeScheduleDate.edit({
          scheduleId: selectedUser.schedule.id,
          scheduleType: CourseEntityScheduleEnum.USER,
          date: data.date,
          type: CourseScheduleType.STATIC,
        });

        await refetch();
        setSelectedUser(undefined);
        setShowChangeEnrollDateModal(false);
        toast?.success(
          t('toast.success'),
          t('courses.schedule.date.change.success'),
        );
      } catch (e) {
        handleAxiosError(e as Error | AxiosError, toast);
      }
    }
  };

  const usersSchedules = useUsersSchedules();
  const handleEnrollUser = async (data: EnrollFormValues) => {
    if (isDemoAccount) return;
    if (course && selectedUser) {
      try {
        await usersSchedules.add({
          courses: [course.id],
          users: [selectedUser.id],
          date: data.date,
          type: data.type,
        });

        await refetch();
        toast?.success(
          t('toast.success'),
          course.type !== MaterialType.RESOURCE &&
            moment(data.date).isSame(moment(), 'day')
            ? t('courses.user.enroll.success.notify')
            : t('courses.user.enroll.success'),
        );

        hubspotTrack(
          HubspotProperty.NEW_USER_ENROLLED_TO_A_COURSE,
          HubspotValue.YES,
        );

        setSelectedUser(undefined);
        setShowEnrollModal(false);
      } catch (e) {
        handleAxiosError(e as Error | AxiosError, toast);
      }
    }
  };

  const deleteEnrollment = useDeleteEnrollment();
  const handleDeleteEnrollment = async (
    courseId: string,
    courseUser: CourseUser,
  ) => {
    if (isDemoAccount) return;
    try {
      await deleteEnrollment.post({
        courseId,
        request: { [CourseEntityEnrollEnum.USERS]: [courseUser.id] },
      });

      await refetch();
      toast?.success(t('toast.success'), t('courses.user.unenroll.success'));

      setSelectedUser(undefined);
      setShowEnrollModal(false);
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  const completeCourse = useForceComplete();
  const handleForceComplete = async (data: ForceCompleteFormValues) => {
    if (isDemoAccount) return;
    if (!selectedUser?.id || !courseId) return;
    try {
      await completeCourse.complete({
        courseId,
        userId: selectedUser.id,
        score: data.score,
        accountId: account?.id as string,
      });
      refetch();
      setShowForceCompleteModal(false);
      toast?.success(
        t('toast.success'),
        t('content.forceComplete.success', { course: course?.name }),
      );
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  const menuItems = (courseUser: CourseUser) => {
    const menu: MenuItem[] = [];

    if (
      courseUser.status !== UserStatusEnum.ENROLLED &&
      courseUser.status !== UserStatusEnum.SCHEDULED
    ) {
      menu.push({
        label: t('generic.enroll'),
        icon: 'pi pi-calendar-plus',
        command: () => {
          setSelectedUser(courseUser);
          setShowEnrollModal(true);
        },
      });
    }

    if (
      courseUser.schedule &&
      !courseUser.schedule.executedAt &&
      !courseUser.enrollment
    ) {
      menu.push({
        label: t('courses.change.enrollDate'),
        icon: 'pi pi-calendar',
        command: () => {
          setSelectedUser(courseUser);
          setShowChangeEnrollDateModal(true);
        },
      });
    }

    if (!courseUser?.enrollment?.unEnrolledAt) {
      menu.push({
        label: t('generic.unenroll'),
        icon: 'pi pi-times',
        command: isDemoAccount
          ? undefined
          : () => {
              setSelectedUser(courseUser);
              setDialogData({
                type: DialogType.CONFIRMATION,
                show: true,
                header: t('dialog.deleteEnrollment'),
                message: t('dialog.deleteEnrollment.user', {
                  name: `${courseUser?.firstName} ${courseUser?.lastName}`,
                }),
                onAccept: async () => {
                  if (!courseUser?.enrollment || !courseId) return;
                  setSelectedUser(courseUser);
                  await handleDeleteEnrollment(courseId, courseUser);
                },
              });
            },
      });
    }

    if (
      ![
        UserStatusEnum.COMPLETED,
        UserStatusEnum.FORCE_COMPLETED,
        UserStatusEnum.SCHEDULED,
        UserStatusEnum.NOT_SCHEDULED,
      ].includes(courseUser.status) &&
      !courseUser?.enrollment?.unEnrolledAt &&
      can(Actions.UPDATE, Subjects.COURSE_FORCE_COMPLETE) &&
      !isMaterial
    ) {
      menu.push({
        label: t('course.forceComplete'),
        icon: 'pi pi-check',
        command: () => {
          setShowForceCompleteModal(true);
          setSelectedUser(courseUser);
        },
      });
    }

    return menu;
  };

  const columns: DataTableColumnType[] = [
    {
      field: 'firstName',
      header: t('user'),
      sortable: true,
      filterable: false,
      style: {
        minWidth: '200px',
      },
      render: (row: CourseUser) => {
        return (
          <FlexContainer
            direction="column"
            className="group-cell"
            align="flex-start"
          >
            <span>
              {row.firstName} {row.lastName}
            </span>
            <span className="secondary-text">{row?.email}</span>
          </FlexContainer>
        );
      },
    },
    {
      field: 'role',
      header: t('generic.role'),
      sortable: false,
      filterable: true,
      filters: {
        type: FilterTypeEnum.SELECT,
        options: notSystemRoleOptions(t),
      },
      className: 'no-padding',
      render: (row: CourseUser) => {
        return (
          <FlexContainer direction="column" className="group-cell">
            <div className="group-row">{roleTranslations(row?.role, t)}</div>
          </FlexContainer>
        );
      },
    },
    {
      field: 'enrollDate',
      header: t('generic.enrollDate'),
      sortable: true,
      filterable: true,
      filters: {
        label: t('filter.byDate'),
        field: FilterNamesEnum.COURSE_ENTITY_UPCOMING_DATE,
        type: FilterTypeEnum.SELECT_UPCOMING_PAST,
        placeholder: t('filter.date'),
      },
      className: 'no-padding',
      render: (row: CourseUser) => {
        return (
          <FlexContainer direction="column" className="group-cell">
            {row?.enrollDate ? (
              <div className="group-row">
                <FormatDate
                  format={DateFormats.TIMEDATE}
                  date={row?.enrollDate}
                />
              </div>
            ) : (
              <div className="group-row">&#8212;</div>
            )}
          </FlexContainer>
        );
      },
    },
    {
      field: 'enrollment.unEnrolledAt',
      header: t('generic.unEnrolledAt'),
      sortable: true,
      filterable: true,
      filters: {
        label: t('filter.unEnrolled'),
        field: FilterNamesEnum.COURSE_ENTITY_USER_UNENROLLED,
        type: FilterTypeEnum.SELECT_YES_NO,
        placeholder: t('generic.select'),
      },
      className: 'no-padding',
      render: (row: CourseUser) => {
        return (
          <FlexContainer direction="column" className="group-cell">
            {row?.enrollment?.unEnrolledAt ? (
              <div className="group-row">
                <FormatDate
                  format={DateFormats.TIMEDATE}
                  date={row.enrollment.unEnrolledAt}
                />
              </div>
            ) : (
              <div className="group-row">&#8212;</div>
            )}
          </FlexContainer>
        );
      },
    },
    {
      field: 'enrollment.unEnrolledBy',
      sortField: 'enrollment.unEnrolledBy.firstName',
      header: t('generic.unEnrolledBy'),
      sortable: true,
      filterable: false,
      className: 'no-padding',
      render: (row: CourseUser) => {
        return (
          <FlexContainer direction="column" className="group-cell">
            <div className="group-row">
              {displayFullName(row?.enrollment?.unEnrolledBy)}
            </div>
          </FlexContainer>
        );
      },
    },
    ...(account?.courseDueDays
      ? [
          {
            field: 'enrollment.dueDate',
            header: t('generic.dueDate'),
            sortable: true,
            filterable: true,
            filters: {
              label: t('filter.have.overdueContent'),
              field: FilterNamesEnum.COURSE_ENTITY_USER_HAS_DUE_COURSES,
              type: FilterTypeEnum.SELECT_YES_NO,
              placeholder: t('generic.select'),
            },
            className: 'no-padding',
            render: (row: CourseUser) => {
              return (
                <FlexContainer direction="column" className="group-cell">
                  {row?.enrollment?.dueDate ? (
                    <div className="group-row">
                      <FormatDate
                        format={DateFormats.TIMEDATE}
                        date={row?.enrollment.dueDate}
                      />
                    </div>
                  ) : (
                    <div className="group-row">&#8212;</div>
                  )}
                </FlexContainer>
              );
            },
          },
        ]
      : []),
    {
      field: 'inheritedSchedule',
      header: t('courses.inheritedFrom'),
      sortable: false,
      filterable: false,
      className: 'no-padding',
      render: (row: CourseUser) => {
        return (
          <FlexContainer direction="column" className="group-cell">
            <div className="group-row">
              {row?.schedule?.source === CourseEntityScheduleEnum.GROUP ? (
                <span>{t('group')}</span>
              ) : row?.schedule?.source === CourseEntityScheduleEnum.BRANCH ? (
                <span>{t('branch')}</span>
              ) : row?.schedule?.source === CourseEntityScheduleEnum.ACCOUNT ? (
                <span>{t('account')}</span>
              ) : (
                <div className="group-row">&#8212;</div>
              )}
            </div>
          </FlexContainer>
        );
      },
    },
    {
      field: 'status',
      header: t('generic.status'),
      sortable: true,
      filterable: true,
      filters: {
        label: t('filter.byStatus'),
        field: FilterNamesEnum.ENTITY_BY_STATUS,
        type: FilterTypeEnum.MULTI_SELECT,
        options: userStatusOptions(t),
      },
      className: 'no-padding',
      render: (row: CourseUser) => {
        return (
          <FlexContainer direction="column" className="group-cell">
            <div className="group-row">
              {determineUserStatusColor(row?.status, t)}
            </div>
          </FlexContainer>
        );
      },
    },
    {
      field: 'actions',
      header: t('generic.actions'),
      sortable: false,
      filterable: false,
      className: 'no-padding',
      style: {
        width: '80px',
        textAlign: 'center',
      },
      render: (row: CourseUser) => {
        return (
          <FlexContainer direction="column" className="group-cell">
            <div className="group-row justify-content-center">
              <DataTableActions
                menuItems={menuItems(row)}
                disabled={menuItems(row).length < 1}
              />
            </div>
          </FlexContainer>
        );
      },
    },
  ];

  // Set the preselected columns
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
  const defaultVisibleColumns = columns.map((column) => column.field);
  const alwaysVisibleColumns = ['name', 'actions'];
  //

  const hasBlockEmailsBanner = showBlockEmailsMessage(account);

  const toolbar = (
    <DataTableToolbar justify="space-between">
      <DataTableFilters
        filters={getFiltersFromColumns(columns)}
        onFilter={onFilter}
        className="flex-initial"
        tableName={
          isMaterial
            ? TableNamesEnum.MATERIAL_USERS
            : TableNamesEnum.COURSE_USERS
        }
      />
      <div className="p-input-icon-left flex-auto min-w-300">
        <InputText
          className="w-full"
          onInput={debouncedSetMultiSearchValue}
          placeholder={t('courses.users.search')}
          autoComplete="off"
        />
        <i className="pi pi-search" />
      </div>
      <DataTableColumnsMultiselect
        columns={columns}
        tableName={
          isMaterial
            ? TableNamesEnum.MATERIAL_USERS
            : TableNamesEnum.COURSE_USERS
        }
        visibleColumns={visibleColumns}
        setVisibleColumns={setVisibleColumns}
        defaultVisibleColumns={defaultVisibleColumns}
        alwaysVisibleColumns={alwaysVisibleColumns}
      />
    </DataTableToolbar>
  );

  return (
    <>
      {isCourseLoading && !course && (
        <FlexContainer direction="column" className="mt-5">
          <ProgressSpinner />
          <h3>
            {isMaterial
              ? t('material.users.loading')
              : t('content.users.loading')}
          </h3>
        </FlexContainer>
      )}
      {!isCourseLoading && course && (
        <>
          <CoursesActionHeader
            course={course}
            onTriggerRefetch={refetch}
            hasBlockEmailsBanner={hasBlockEmailsBanner}
          />
          {course && <CoursesTabs course={course} />}
          <DataTable
            dataKey="dataId"
            data={courseUsers?.result.map((x) => ({
              ...x,
              dataId: getCourseUserRowKey(x),
            }))}
            count={courseUsers?.count as number}
            isLoading={isCourseUsersLoading}
            toolbar={toolbar}
            columns={columns}
            visibleColumns={visibleColumns}
            onPage={onPage}
            rows={take}
            skip={skip}
            onSort={onSort}
            sort={sort}
            classNames="multiline-datatable with-actions"
          />

          <EnrollModal
            type={CourseEnrollmentTypeEnum.ENROLL}
            account={account as Account}
            user={selectedUser as unknown as User}
            enrollType={CourseEntityScheduleEnum.USER}
            visible={showEnrollModal && !!selectedUser}
            onSubmit={handleEnrollUser}
            onHide={() => setShowEnrollModal(false)}
            course={course}
            isDemoAccount={isDemoAccount}
          />

          <ChangeEnrollDateModal
            date={selectedUser?.schedule?.date ?? undefined}
            visible={showChangeEnrollDateModal && !!selectedUser}
            onSubmit={handleChangeScheduleDate}
            onHide={() => setSelectedUser(undefined)}
            isDemoAccount={isDemoAccount}
          />

          <ForceCompleteModal
            visible={showForceCompleteModal}
            onSubmit={handleForceComplete}
            onHide={() => setShowForceCompleteModal(false)}
            userName={`${selectedUser?.firstName} ${selectedUser?.lastName}`}
            courseName={course.name}
            isDemoAccount={isDemoAccount}
          />
        </>
      )}
    </>
  );
};
